import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiBook, FiPlus, FiFilter, FiSearch, FiEdit2, FiTrash2, FiArrowLeft, FiCheckCircle, FiFileText, FiUsers, FiBriefcase, FiAward, FiShield, FiSave, FiX, FiHelpCircle, FiLayout } = FiIcons;

const CATEGORIES = [
  { id: 'brukere', label: 'Brukere / Bedrifter', icon: FiUsers, color: 'blue' },
  { id: 'partnere', label: 'Partnere', icon: FiAward, color: 'purple' },
  { id: 'selgere', label: 'Selgere', icon: FiBriefcase, color: 'green' },
  { id: 'internt', label: 'Internt / SuperAdmin', icon: FiShield, color: 'red' }
];

const CONTENT_TYPES = [
  { id: 'guide', label: 'Veileder / Artikkel', icon: FiBook },
  { id: 'qna', label: 'Spørsmål & Svar (Q&A)', icon: FiHelpCircle },
  { id: 'template', label: 'Mal / Tekstforslag', icon: FiLayout },
  { id: 'pitch', label: 'Salgspitch', icon: FiAward }
];

const GuideManagement = () => {
  const navigate = useNavigate();
  const { guides = [], addGuide, updateGuide, deleteGuide } = useData();
  const [activeTab, setActiveTab] = useState('brukere');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGuide, setEditingGuide] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    categories: [],
    type: 'guide',
    description: '',
    content: ''
  });

  const filteredGuides = guides.filter(guide => {
    const guideCats = Array.isArray(guide.categories) ? guide.categories : [guide.category];
    const inCategory = guideCats.includes(activeTab);
    const matchesSearch = guide.title.toLowerCase().includes(searchQuery.toLowerCase());
    return inCategory && matchesSearch;
  });

  const handleAddNew = () => {
    setEditingGuide(null);
    setFormData({ title: '', categories: [activeTab], type: 'guide', description: '', content: '' });
    setShowCreateModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingGuide) updateGuide(editingGuide.id, formData);
    else addGuide(formData);
    setShowCreateModal(false);
  };

  const handleEdit = (guide) => {
    setEditingGuide(guide);
    setFormData({
        title: guide.title,
        categories: Array.isArray(guide.categories) ? guide.categories : [guide.category],
        type: guide.type,
        description: guide.description,
        content: guide.content
    });
    setShowCreateModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12 font-sans">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 px-8 py-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
            <button onClick={() => navigate('/superadmin')} className="p-2 hover:bg-gray-100 rounded-lg"><SafeIcon icon={FiArrowLeft} /></button>
            <h1 className="text-xl font-black text-gray-900">Veiledere & Innhold</h1>
        </div>
        <button onClick={handleAddNew} className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-indigo-700">
            <SafeIcon icon={FiPlus} /> Nytt Innhold
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Categories */}
        <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
            {CATEGORIES.map(cat => (
                <button 
                    key={cat.id} 
                    onClick={() => setActiveTab(cat.id)}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all whitespace-nowrap ${activeTab === cat.id ? `bg-${cat.color}-600 text-white shadow-lg` : 'bg-white text-gray-500 hover:bg-gray-100'}`}
                >
                    <SafeIcon icon={cat.icon} /> {cat.label}
                </button>
            ))}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGuides.map(guide => (
                <div key={guide.id} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all group">
                    <div className="flex justify-between items-start mb-4">
                        <div className="p-3 bg-gray-50 rounded-xl text-gray-500 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
                            <SafeIcon icon={CONTENT_TYPES.find(t => t.id === guide.type)?.icon || FiFileText} className="text-xl" />
                        </div>
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handleEdit(guide)} className="p-2 hover:bg-gray-100 rounded-lg text-blue-600"><SafeIcon icon={FiEdit2} /></button>
                            <button onClick={() => deleteGuide(guide.id)} className="p-2 hover:bg-gray-100 rounded-lg text-red-600"><SafeIcon icon={FiTrash2} /></button>
                        </div>
                    </div>
                    <h3 className="font-bold text-lg text-gray-900 mb-2">{guide.title}</h3>
                    <p className="text-sm text-gray-500 line-clamp-3 mb-4">{guide.description}</p>
                    <div className="flex gap-2">
                        <span className="text-[10px] font-black uppercase bg-gray-100 px-2 py-1 rounded text-gray-500">{guide.type}</span>
                    </div>
                </div>
            ))}
        </div>
      </div>

      {/* Basic Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-2xl rounded-2xl p-8 max-h-[90vh] overflow-y-auto">
                <h2 className="text-2xl font-bold mb-6">{editingGuide ? 'Rediger' : 'Ny'} Veileder</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="text" placeholder="Tittel" className="w-full p-3 border rounded-xl font-bold" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                    <textarea placeholder="Beskrivelse" className="w-full p-3 border rounded-xl" rows={2} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
                    <textarea placeholder="Innhold (Markdown støttes)" className="w-full p-3 border rounded-xl font-mono text-sm" rows={10} value={formData.content} onChange={e => setFormData({...formData, content: e.target.value})} />
                    <div className="flex justify-end gap-3 pt-4">
                        <button type="button" onClick={() => setShowCreateModal(false)} className="px-6 py-3 rounded-xl border font-bold text-gray-600">Avbryt</button>
                        <button type="submit" className="px-6 py-3 rounded-xl bg-indigo-600 text-white font-bold">Lagre</button>
                    </div>
                </form>
            </div>
        </div>
      )}
    </div>
  );
};

export default GuideManagement;